import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  AuthenticationProvider,
  PermissionsProvider,
  useAuthentication,
} from '@src/features/auth';
import {
  getDateAdapterLocale,
  mapBackendLanguageToApp,
  useAPIClient
} from '@src/shared';
import { Composer, ErrorFallback } from '@src/shared/lib';
import { QueryClientProvider } from '@tanstack/react-query';
import { CatchBoundary, RouterProvider } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Devtools } from './devtools';
import { NotiFicationDialogProvider } from './notifications/dialog';
import { ToastProvider } from './notifications/toast';
import { queryClient } from './providers';
import router from './router';
import { theme } from './themes';
import './styles';
import './fonts';

function InnerApp() {
  const auth = useAuthentication();
  const apiClient = useAPIClient();
  return <RouterProvider router={router} context={{ auth, apiClient }} />;
}

function AuthLanguageSync() {
  const auth = useAuthentication();
  const { i18n } = useTranslation();

  useEffect(() => {
    const nextLanguage = mapBackendLanguageToApp(auth.user?.language);
    if (nextLanguage !== i18n.resolvedLanguage) {
      void i18n.changeLanguage(nextLanguage);
    }
  }, [auth.user?.language, i18n]);

  return null;
}

function App() {
  const { i18n } = useTranslation();
  const appLanguage = mapBackendLanguageToApp(i18n.resolvedLanguage);

  const localizationProps = useMemo(
    () => ({
      dateAdapter: AdapterDateFns,
      adapterLocale: getDateAdapterLocale(appLanguage),
    }),
    [appLanguage],
  );

  const providers = useMemo(
    () => [
      { element: ThemeProvider, props: { theme: theme } },
      { element: StyledEngineProvider, props: { injectFirst: true } },
      { element: LocalizationProvider, props: localizationProps },
      { element: AuthenticationProvider, props: {} },
      { element: PermissionsProvider, props: {} },
      { element: QueryClientProvider, props: { client: queryClient } },
      { element: ToastProvider, props: {} },
      { element: NotiFicationDialogProvider, props: {} },
    ],
    [localizationProps],
  );

  return (
    <CatchBoundary getResetKey={() => 'reset'} errorComponent={ErrorFallback}>
      <Composer providers={providers}>
        <CssBaseline />
        <AuthLanguageSync />
        <InnerApp />
        <Devtools router={router} />
      </Composer>
    </CatchBoundary>
  );
}

export default App;
